import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';
import { Outlet, generatePath, useOutletContext } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { NewPageHeader } from '@sorare/core/src/components/navigation/NewPageHeader';
import { PageHeader as OldPageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  PlayerPageTabs,
  Tabs,
  Props as TabsProps,
} from '@sorare/core/src/components/seo/Player/Tabs';
import {
  FOOTBALL_PLAYERS_SLUG,
  FOOTBALL_PLAYERS_SLUG_CARDS,
  FOOTBALL_PLAYERS_SLUG_FIXTURES,
  FOOTBALL_PLAYERS_SLUG_TRANSACTIONS,
} from '@sorare/core/src/constants/__generated__/routes';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { isType } from '@sorare/core/src/lib/gql';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { Transactions } from '@sorare/marketplace/src/components/player/Transactions';

import {
  FootballSeoPlayerQuery,
  FootballSeoPlayerQueryVariables,
} from './__generated__/layout.graphql';
import { PageParams } from './__generated__/routeParams';

const FOOTBALL_SEO_PLAYER_QUERY = gql`
  query FootballSeoPlayerQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      displayName
      avatarPictureUrl
      ... on Player {
        slug
        pastGames(first: 1) {
          nodes {
            id
          }
        }
      }
      ...Transactions_anyPlayerInterface
    }
  }
  ${
    // eslint-disable-next-line sorare/no-detached-fragments
    Transactions.fragments.anyPlayer
  }
` as TypedDocumentNode<FootballSeoPlayerQuery, FootballSeoPlayerQueryVariables>;

type ContextType = {
  player: FootballSeoPlayerQuery['anyPlayer'];
  slug: string;
};

export const useLayoutData = () => {
  return useOutletContext<ContextType>();
};

export const loader = queryLoader(
  FOOTBALL_SEO_PLAYER_QUERY,
  ({ slug }: PageParams) => ({ slug })
);

const PlayersSlugLayout = () => {
  const { data } = useQueryLoader<FootballSeoPlayerQuery>();
  const {
    flags: { useNewNav = false },
  } = useFeatureFlags();
  const { slug } = useTypedParams<PageParams>();

  const player = data?.anyPlayer;

  const tabs: TabsProps['tabs'] = useMemo(() => {
    return {
      [PlayerPageTabs.OVERVIEW]: {
        to: generatePath(FOOTBALL_PLAYERS_SLUG, {
          slug,
        }),
      },
      [PlayerPageTabs.FIXTURES]: {
        to: generatePath(FOOTBALL_PLAYERS_SLUG_FIXTURES, {
          slug,
        }),
      },
      [PlayerPageTabs.TRANSACTIONS]: {
        to: generatePath(FOOTBALL_PLAYERS_SLUG_TRANSACTIONS, {
          slug,
        }),
      },
      [PlayerPageTabs.CARDS]: {
        to: generatePath(FOOTBALL_PLAYERS_SLUG_CARDS, {
          slug,
        }),
      },
    };
  }, [slug]);

  if (!player || !isType(player, 'Player')) return null;

  const hasEnoughSEOData =
    player.cardSupply.length > 0 || player.pastGames.nodes.length > 0;

  const PageHeader = useNewNav ? NewPageHeader : OldPageHeader;
  return (
    <>
      <PageHeader text={player.displayName} />
      {!hasEnoughSEOData && <NoIndex />}
      <ContentWithStickyHeader header={<Tabs tabs={tabs} />}>
        <Outlet context={{ player, slug } satisfies ContextType} />
      </ContentWithStickyHeader>
    </>
  );
};

export default PlayersSlugLayout;
